import clsx from 'clsx';
import { type PropsWithChildren } from 'react';

import { useNavigateSpinButton } from './hooks/useNavigateSpinButton.js';
import { useValueOverflow } from './hooks/useValueOverflow.js';
import { SpinButtonGroupSeparator } from './SpinButtonGroupSeparator.js';
import type { SpinButtonGroupProps } from './types.js';
import { useBehavioralTrackingProps } from '../../../core/hooks/useBehavioralTrackingProps.js';
import { NumberSpinButton } from '../number-spin-button/NumberSpinButton.js';
import { spinButtonGroupCSS } from '../SpinButton.sty.js';
import { TextSpinButton } from '../text-spin-button/TextSpinButton.js';

const InternalSpinButtonGroup = ({
  children,
  valueOverflow = false,
  onLeadingEdge,
  onTrailingEdge,
  onTrailingOverflow,
  className: consumerClassName,
  style: consumerStyle,
  'data-testid': dataTestId,
  'data-dtrum-mask': dataDtrumMask,
  'data-dtrum-allow': dataDtrumAllow,
  ...remainingProps
}: PropsWithChildren<SpinButtonGroupProps>) => {
  const [behavioralTrackingProps] = useBehavioralTrackingProps(remainingProps);
  const { clonedChildren, spinButtonRefs, triggerRefs } = useValueOverflow(
    children,
    valueOverflow,
    onTrailingOverflow,
    onTrailingEdge,
  );

  const { onKeyDownHandler } = useNavigateSpinButton(
    spinButtonRefs,
    triggerRefs,
    onLeadingEdge,
    onTrailingEdge,
  );

  return (
    <span
      className={clsx(spinButtonGroupCSS, consumerClassName)}
      data-valuetype="iso8601"
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...behavioralTrackingProps}
      onKeyDown={onKeyDownHandler}
    >
      {clonedChildren}
    </span>
  );
};

/**
 * Use the _SpinButtonGroup to combine text and number spinbuttons and provide ootb navigation between them.
 * @internal
 */
export const SpinButtonGroup = Object.assign(InternalSpinButtonGroup, {
  Number: NumberSpinButton,
  Text: TextSpinButton,
  Separator: SpinButtonGroupSeparator,
});

(
  SpinButtonGroup as typeof SpinButtonGroup & { displayName: string }
).displayName = '_SpinButtonGroup';
